import { ref, watch, computed } from '@vue/composition-api';
import store from '@/store';
import { title } from '@core/utils/filter';

// Notification
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function useCustomerList() {
  // Use toast
  const toast = useToast();

  const refCustomerListTable = ref(null);

  const isTableLoading = ref(false);

  const selectedRows = ref([]);
  const selectAll = ref(false);
  const fetching = ref(false);

  // Table Handlers
  const tableColumns = [
    { key: 'select', label: '', sortable: false, thStyle: { width: '40px' } },
    {
      key: 'code',
      sortable: false,
      stickyColumn: true,
      label: 'Code',
      thStyle: { minWidth: '80px' },
    },
    { key: 'name', sortable: false, label: 'Name', thStyle: { minWidth: '150px' } },
    { key: 'shortName', sortable: false, label: 'Short Name', thStyle: { minWidth: '120px' } },
    { key: 'cellNumber', sortable: false, label: 'Cell #', thStyle: { minWidth: '120px' } },
    { key: 'category', sortable: false, label: 'Category', thStyle: { minWidth: '100px' } },
    { key: 'isActive', sortable: false, label: 'Status', thStyle: { minWidth: '80px' } },
    { key: 'email', sortable: false, label: 'Email', thStyle: { minWidth: '180px' } },
    { key: 'ntnNumber', sortable: false, label: 'NTN #', thStyle: { minWidth: '100px' } },
    { key: 'gstNumber', sortable: false, label: 'STRN #', thStyle: { minWidth: '100px' } },
    { key: 'address', sortable: false, label: 'Address', thStyle: { minWidth: '200px' } },
    { key: 'province', sortable: false, label: 'Province', thStyle: { minWidth: '100px' } },
    { key: 'cnicNumber', sortable: false, label: 'CNIC #', thStyle: { minWidth: '120px' } },
    {
      key: 'contactPerson',
      sortable: false,
      label: 'Contact Person',
      thStyle: { minWidth: '120px' },
    },
    { key: 'whatsappOrUAN', sortable: false, label: 'Whatsapp #', thStyle: { minWidth: '120px' } },
    { key: 'website', sortable: false, label: 'Website', thStyle: { minWidth: '150px' } },
    {
      key: 'commissionPercent',
      sortable: false,
      label: 'Commission %',
      thStyle: { minWidth: '100px' },
    },
    { key: 'actions', sortable: false, thStyle: { minWidth: '120px' }, stickyColumn: true },
  ];
  const perPage = ref(10);
  const totalCustomers = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref('');

  const dataMeta = computed(() => {
    const localItemsCount = refCustomerListTable.value
      ? refCustomerListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalCustomers.value,
    };
  });

  const refetchData = () => {
    refCustomerListTable.value.refresh();
  };

  // Debounced search function
  const debouncedSearch = debounce(() => {
    refetchData();
  }, 500); // 500ms delay

  // Watch for page and perPage changes (immediate)
  watch([currentPage, perPage], () => {
    refetchData();
  });

  // Watch for search query changes (debounced)
  watch(searchQuery, () => {
    if (selectAll.value) {
      selectAll.value = false;
    }
    selectedRows.value = [];
    if (refCustomerListTable.value) {
      refCustomerListTable.value.clearSelected();
    }
    debouncedSearch();
  });


  const fetchCustomers = (ctx, callback) => {
    console.log('=== useCustomerList fetchCustomers()');

    isTableLoading.value = true;

    store
      .dispatch('fbr-invoice-customer/fetchCustomers', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
      })
      .then((response) => {
        console.log('Values ---->', response);

        const { items, pagination } = response;
        console.log('Items: -->', items);
        callback(items);
        totalCustomers.value = pagination.totalCount;

        // Re-apply selection for local items if "Select All" is active
        if (selectAll.value) {
          setTimeout(() => {
            if (refCustomerListTable.value && refCustomerListTable.value.localItems) {
              const pageEligibleRows = refCustomerListTable.value.localItems;
              pageEligibleRows.forEach((row) => {
                const index = refCustomerListTable.value.localItems.findIndex((r) => r.id === row.id);
                if (index !== -1) {
                  refCustomerListTable.value.selectRow(index);
                }
              });
            }
          }, 100);
        }
      })
      .catch((error) => {
        console.log('=== useCustomerList fetchCustomers() error', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching Customers list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      })
      .finally(() => {
        isTableLoading.value = false;
        fetching.value = false;
      });
  };

  const toggleSelectAll = async () => {
    if (selectAll.value) {
      fetching.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-customer/fetchCustomers', {
          q: searchQuery.value,
          perPage: 99999, // Get all available data
          page: 1,
        });

        const allItems = response.items || [];
        selectedRows.value = allItems;

        // Automatically select the UI component rendering rows on the current page
        if (refCustomerListTable.value && refCustomerListTable.value.localItems) {
          const pageEligibleRows = refCustomerListTable.value.localItems;
          pageEligibleRows.forEach((row) => {
            const index = refCustomerListTable.value.localItems.findIndex((r) => r.id === row.id);
            if (index !== -1) {
              refCustomerListTable.value.selectRow(index);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching all items for Select All:', error);
      } finally {
        fetching.value = false;
      }
    } else {
      selectedRows.value = [];
      if (refCustomerListTable.value) {
        refCustomerListTable.value.clearSelected();
      }
    }
  };

  return {
    fetchCustomers,
    tableColumns,
    perPage,
    currentPage,
    totalCustomers,
    dataMeta,
    perPageOptions,
    searchQuery,
    refCustomerListTable,

    // Loading state
    isTableLoading,

    selectedRows,
    selectAll,
    toggleSelectAll,

    refetchData,
  };
}
